import {
  getFirstAttributeValue,
  getPrice,
  rpcCall,
} from '@scayle/storefront-nuxt'
import { readonly } from 'vue'

import useUiState from './ui/useUiState'
import { getSizeLabel } from '~/types/variant'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

import type {
  GetBackInStockSubscriptionsData,
  SubscribeToBackInStockData,
} from '~/rpcMethods'
import type { Locale } from '~/config/locales'
import { getLocaleIsoCode } from '~/config/locales'

const isLoading = ref(false)
const showError = ref(false)
const showSuccess = ref(false)
const doiRequired = ref(true)

export default () => {
  isLoading.value = false
  showError.value = false
  showSuccess.value = false

  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const currentShop = useCurrentShop()
  const { activeVariant, activeProduct, showBackInStock } = useUiState()
  const { trackBackInStockRequest } = useTrackingEvents()

  const { user } = useAgsUser()

  const email = computed(() => user.value?.email)

  const sendBackInStockRequest = async (
    user: Omit<SubscribeToBackInStockData['user'], 'businessUnit'>,
  ) => {
    isLoading.value = true
    showError.value = false
    showSuccess.value = false

    try {
      const item = {
        color:
          getFirstAttributeValue(activeProduct.value.attributes, 'color')
            ?.label || '',
        productName:
          getFirstAttributeValue(activeProduct.value.attributes, 'name')
            ?.label || '',
        size: getSizeLabel(activeVariant.value),
        sku: activeVariant.value.referenceKey || '',
        productUrl: `${window.location.origin}${route.fullPath}`,
        variantId: activeVariant.value.id.toString(),
        imageUrl:
          nuxtApp.$config.public.imageBaseUrl +
          getProductImage(activeProduct.value.images, '_5').hash,
        price: getPrice(activeVariant.value),
      }

      const businessUnit = getLocaleIsoCode(currentShop.value.locale as Locale)
      const callData: SubscribeToBackInStockData = {
        user: { ...user, businessUnit },
        item,
      }

      trackBackInStockRequest(activeProduct.value, activeVariant.value)

      const call = rpcCall(nuxtApp, 'subscribeToBackInStock', currentShop.value)
      await call(callData)

      showSuccess.value = true
    } catch {
      showError.value = true
    } finally {
      isLoading.value = false
    }
  }

  const checkIfDoiRequired = async () => {
    if (!email.value) {
      doiRequired.value = true
      return
    }

    try {
      const businessUnit = getLocaleIsoCode(currentShop.value.locale as Locale)
      const callData: GetBackInStockSubscriptionsData = {
        email: email.value,
        businessUnit,
      }

      const call = rpcCall(
        nuxtApp,
        'getBackInStockSubscriptions',
        currentShop.value,
      )
      const response = await call(callData)

      doiRequired.value = !!response.doi
    } catch {
      doiRequired.value = true
    }
  }

  watch(email, () => checkIfDoiRequired(), { immediate: true })

  watch(activeVariant, (variant) => {
    if (!showBackInStock.value && !variant) {
      return
    }

    showBackInStock.value =
      variant &&
      variant.stock.quantity === 0 &&
      !variant.stock.isSellableWithoutStock
  })

  return {
    sendBackInStockRequest,
    product: activeProduct,
    variant: activeVariant,
    showBackInStock,
    doiRequired,
    isLoading,
    showError: readonly(showError),
    showSuccess,
    checkIfDoiRequired,
  }
}
